import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth.tsx";
import { checkAdminAccess, type AdminAccessLevel } from "../lib/admin-permissions.ts";
import { PageLoader } from "./PageLoader.tsx";
import { Layout } from "./layout/index.ts";
import { AdminErrorBoundary } from "./ErrorBoundary.tsx";

/**
 * Permission-based access control for admin routes.
 * Same Layout as main app — admin pages feel like part of the app.
 *
 * SECURITY: Returns early (blocks render) when not authorized.
 * Never renders children until auth check completes and passes.
 */

type AccessState =
  | { status: "loading" }
  | { status: "no-account"; redirectTo: "/" }
  | { status: "no-permission"; redirectTo: "/" | "/admin" }
  | { status: "allowed" };

function useAdminAccess(level: AdminAccessLevel): AccessState {
  const { account, isLoading } = useAuth();

  if (isLoading) {
    return { status: "loading" };
  }

  if (!account) {
    return { status: "no-account", redirectTo: "/" };
  }

  const hasAccess = checkAdminAccess(level, account.permissions);
  if (!hasAccess) {
    const redirectTo = level === "admin-panel" ? "/" : "/admin";
    return { status: "no-permission", redirectTo };
  }

  return { status: "allowed" };
}

interface AdminOperatorRouteProps {
  children: ReactNode;
}

/**
 * Protects admin panel entry (/admin home).
 * Allows any permission that grants access to at least one admin section.
 * Redirects to / when not logged in or no access.
 */
export function AdminPanelRoute({ children }: AdminOperatorRouteProps) {
  const accessState = useAdminAccess("admin-panel");

  switch (accessState.status) {
    case "loading":
      return (
        <Layout>
          <PageLoader message="Проверка доступа..." />
        </Layout>
      );
    
    case "no-account":
    case "no-permission":
      return <Navigate to={accessState.redirectTo} replace />;

    case "allowed":
      return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
  }
}

/**
 * Protects operator routes (payments, orders, cash tickets).
 * Redirects to / when not logged in, to /admin when no operator access.
 * 
 * SECURITY: Never renders children until auth check passes.
 */
export function AdminOperatorRoute({ children }: AdminOperatorRouteProps) {
  const accessState = useAdminAccess("operator");

  switch (accessState.status) {
    case "loading":
      return (
        <Layout>
          <PageLoader message="Проверка доступа..." />
        </Layout>
      );
    
    case "no-account":
    case "no-permission":
      // Use Navigate component for immediate redirect (no flash of content)
      return <Navigate to={accessState.redirectTo} replace />;
    
    case "allowed":
      return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
  }
}

interface AdminAdminRouteProps {
  children: ReactNode;
}

/**
 * Protects admin-only routes (events, ticket types).
 * Redirects to /admin when not allowed.
 * 
 * SECURITY: Never renders children until auth check passes.
 */
export function AdminAdminRoute({ children }: AdminAdminRouteProps) {
  const accessState = useAdminAccess("admin");

  switch (accessState.status) {
    case "loading":
      return (
        <Layout>
          <PageLoader message="Проверка доступа..." />
        </Layout>
      );
    
    case "no-account":
    case "no-permission":
      // Use Navigate component for immediate redirect (no flash of content)
      return <Navigate to={accessState.redirectTo} replace />;
    
    case "allowed":
      return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
  }
}

interface AdminRaffleRouteProps {
  children: ReactNode;
}

/**
 * Protects raffle management routes.
 * Requires raffle.manage OR admin.full permission.
 * Redirects to /admin when not allowed.
 * 
 * SECURITY: Never renders children until auth check passes.
 */
export function AdminRaffleRoute({ children }: AdminRaffleRouteProps) {
  const accessState = useAdminAccess("raffle");

  switch (accessState.status) {
    case "loading":
      return (
        <Layout>
          <PageLoader message="Проверка доступа..." />
        </Layout>
      );
    
    case "no-account":
    case "no-permission":
      // Use Navigate component for immediate redirect (no flash of content)
      return <Navigate to={accessState.redirectTo} replace />;
    
    case "allowed":
      return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
  }
}

interface AdminTicketRouteProps {
  children: ReactNode;
}

/**
 * Protects ticket management routes (validation, cash sales).
 * Requires tickets.scan OR tickets.issue OR admin.full permission.
 * Redirects to /admin when not allowed.
 * 
 * SECURITY: Never renders children until auth check passes.
 */
export function AdminTicketRoute({ children }: AdminTicketRouteProps) {
  const accessState = useAdminAccess("tickets");

  switch (accessState.status) {
    case "loading":
      return (
        <Layout>
          <PageLoader message="Проверка доступа..." />
        </Layout>
      );
    
    case "no-account":
    case "no-permission":
      // Use Navigate component for immediate redirect (no flash of content)
      return <Navigate to={accessState.redirectTo} replace />;
    
    case "allowed":
      return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
  }
}

interface AdminTicketIssueRouteProps {
  children: ReactNode;
}

/**
 * Protects cash ticket issuance routes.
 * Requires tickets.issue OR admin.full permission.
 * Redirects to /admin when not allowed.
 * 
 * SECURITY: Never renders children until auth check passes.
 */
export function AdminTicketIssueRoute({ children }: AdminTicketIssueRouteProps) {
  const accessState = useAdminAccess("tickets-issue");

  switch (accessState.status) {
    case "loading":
      return (
        <Layout>
          <PageLoader message="Проверка доступа..." />
        </Layout>
      );
    
    case "no-account":
    case "no-permission":
      // Use Navigate component for immediate redirect (no flash of content)
      return <Navigate to={accessState.redirectTo} replace />;
    
    case "allowed":
      return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
  }
}

interface AdminGenderPartyRouteProps {
  children: ReactNode;
}

/**
 * Protects gender party management routes.
 * Requires gender_party.manage OR admin.full permission.
 * Redirects to /admin when not allowed.
 * 
 * SECURITY: Never renders children until auth check passes.
 */
export function AdminGenderPartyRoute({ children }: AdminGenderPartyRouteProps) {
  const accessState = useAdminAccess("gender-party");

  switch (accessState.status) {
    case "loading":
      return (
        <Layout>
          <PageLoader message="Проверка доступа..." />
        </Layout>
      );
    
    case "no-account":
    case "no-permission":
      // Use Navigate component for immediate redirect (no flash of content)
      return <Navigate to={accessState.redirectTo} replace />;
    
    case "allowed":
      return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
  }
}

interface AdminAcademyRouteProps {
  children: ReactNode;
}

/**
 * Protects academy management routes.
 * Requires academy.manage OR admin.full permission.
 * Redirects to /admin when not allowed.
 * 
 * SECURITY: Never renders children until auth check passes.
 */
export function AdminAcademyRoute({ children }: AdminAcademyRouteProps) {
  const accessState = useAdminAccess("academy");

  switch (accessState.status) {
    case "loading":
      return (
        <Layout>
          <PageLoader message="Проверка доступа..." />
        </Layout>
      );
    
    case "no-account":
    case "no-permission":
      // Use Navigate component for immediate redirect (no flash of content)
      return <Navigate to={accessState.redirectTo} replace />;
    
    case "allowed":
      return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
  }
}
